import { useEffect, useMemo, useRef } from 'react';
import { useWatch } from 'react-hook-form';
import type { UseFormReturn } from 'react-hook-form';
import { useTranslation } from '@tasama/i18n';
import {
  COMPANY_TYPES,
  type ExtendedCompanyDetailsFormValues,
  getFieldRequirements,
  getCompanyDetailsSchema,
  getCompanyDetailsDefaultValues,
} from '../../../schemas/company-details.schema';
import {
  useCompanyTypeFormData,
  useCompanyTypeStore,
  type CompanyTypeStore,
} from '../../../stores/company-type-store';
import type { CompanyType } from '../components/CompanyDetailsTab/types';
import { usePersistentForm } from '../../../hooks/usePersistentForm';

export const COMPANY_DETAILS_STORAGE_KEY = 'portal-company-details';
const STORAGE_KEY = COMPANY_DETAILS_STORAGE_KEY;

interface UseCompanyFormLogicProps {
  companyType: CompanyType;
  initialData?: Partial<ExtendedCompanyDetailsFormValues>;
}

interface UseCompanyFormLogicReturn {
  formData: Partial<ExtendedCompanyDetailsFormValues>;
  errors: Partial<Record<keyof ExtendedCompanyDetailsFormValues, string>>;
  companyTypeData: ReturnType<typeof useCompanyTypeFormData>;
  isValid: boolean;
  handleFieldChange: (
    _field: keyof ExtendedCompanyDetailsFormValues,
    _value: string
  ) => void;
  handleFieldBlur: (_field: keyof ExtendedCompanyDetailsFormValues) => void;
  isFieldRequired: (
    _fieldName: keyof ExtendedCompanyDetailsFormValues
  ) => boolean;
  shouldRenderField: (
    _fieldName: keyof ExtendedCompanyDetailsFormValues
  ) => boolean;
  getFieldLabel: (
    _baseLabel: string,
    _fieldName: keyof ExtendedCompanyDetailsFormValues
  ) => string;
  trigger: UseFormReturn<ExtendedCompanyDetailsFormValues>['trigger'];
  getValues: UseFormReturn<ExtendedCompanyDetailsFormValues>['getValues'];
  clearStorage: () => void;
}

/**
 * Custom hook for company form logic — portal edit flow
 *
 * Replaces the Zustand-based useCompanyFormLogic with react-hook-form +
 * zodResolver. Persists form values to sessionStorage so the user's edits
 * survive a page refresh between steps.
 *
 * Responsibilities:
 *  - Dynamic schema via useMemo (changes when companyType changes)
 *  - sessionStorage persistence via usePersistentForm
 *  - One-time initialisation from initialData (React Query async data)
 *  - Field-level visibility and required logic (company-type-aware)
 */
export const useCompanyFormLogic = ({
  companyType,
  initialData,
}: UseCompanyFormLogicProps): UseCompanyFormLogicReturn => {
  const isFirstInit = useRef(true);
  const { t } = useTranslation('portal');

  // Sync company type into its store so getFieldRequirements / schema see it
  const updateCompanyTypeFormData = useCompanyTypeStore(
    (s: CompanyTypeStore) => s.updateFormData
  );
  useEffect(() => {
    if (companyType) updateCompanyTypeFormData({ companyType });
  }, [companyType, updateCompanyTypeFormData]);

  // Company type data (reactive — used for schema and field logic)
  const companyTypeData = useCompanyTypeFormData();

  // Dynamic schema — rebuilt when company type changes
  const schema = useMemo(
    () => getCompanyDetailsSchema(companyTypeData, t),
    [companyTypeData, t]
  );

  // Type-based default values
  const typeDefaults = useMemo(
    () => getCompanyDetailsDefaultValues(companyTypeData),
    [companyTypeData]
  );

  // Resolve initialDefaultValues: sessionStorage > initialData > type defaults.
  // This is computed once — useForm only reads defaultValues on mount.
  const initialDefaultValues = useMemo(() => {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    const storedValues = stored
      ? (JSON.parse(stored) as Partial<ExtendedCompanyDetailsFormValues>)
      : null;
    if (storedValues) return { ...typeDefaults, ...storedValues };
    if (initialData && Object.keys(initialData).length > 0) {
      return { ...typeDefaults, ...initialData };
    }
    return typeDefaults;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally mount-only

  const formMethods = usePersistentForm<ExtendedCompanyDetailsFormValues>(
    STORAGE_KEY,
    schema as any,
    initialDefaultValues as any
  );
  const { control, setValue, reset, formState, clearStorage } = formMethods;
  const trigger =
    formMethods.trigger as UseFormReturn<ExtendedCompanyDetailsFormValues>['trigger'];
  const getValues =
    formMethods.getValues as UseFormReturn<ExtendedCompanyDetailsFormValues>['getValues'];

  const { errors: rhfErrors, isValid } = formState;

  // When initialData arrives asynchronously from React Query, reset ONCE if:
  //  - We have not yet initialised from real data
  //  - sessionStorage was empty at mount (no in-progress edits to protect)
  useEffect(() => {
    if (!initialData || Object.keys(initialData).length === 0) return;
    if (!isFirstInit.current) return;

    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      // sessionStorage takes precedence — user was mid-edit
      isFirstInit.current = false;
      return;
    }

    reset({ ...typeDefaults, ...initialData } as any);
    isFirstInit.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData]);

  // Derive plain errors record from RHF formState.errors.
  // Not memoized: RHF mutates the errors object in-place when clearing
  // individual field errors (e.g. field-level trigger), so the reference
  // stays the same and useMemo([rhfErrors]) would return a stale value.
  const errors: Partial<
    Record<keyof ExtendedCompanyDetailsFormValues, string>
  > = {};
  for (const [key, err] of Object.entries(rhfErrors)) {
    if (err?.message) {
      errors[key as keyof ExtendedCompanyDetailsFormValues] =
        err.message as string;
    }
  }

  // Current form values as a plain object (replaces store's formData selector)
  const formData = useWatch({ control });

  const handleFieldChange = (
    field: keyof ExtendedCompanyDetailsFormValues,
    value: string
  ) => {
    setValue(field, value as any, { shouldValidate: true, shouldTouch: true });
  };

  const handleFieldBlur = (field: keyof ExtendedCompanyDetailsFormValues) => {
    // Use setValue (same path as handleFieldChange) rather than trigger(field):
    // trigger() on an unregistered field mutates formState.errors in-place,
    // which won't be detected as a reference change by consumers. setValue
    // forces a proper subscription update that triggers a re-render.
    setValue(field, getValues(field as any) as any, { shouldValidate: true });
  };

  const shouldRenderField = (
    fieldName: keyof ExtendedCompanyDetailsFormValues
  ): boolean => {
    switch (fieldName) {
      case 'industrySector':
        return (
          companyType === COMPANY_TYPES.INTERNATIONAL ||
          companyType === COMPANY_TYPES.KSA_PRIVATE
        );
      case 'countryOfHeadquarter':
        return companyType === COMPANY_TYPES.INTERNATIONAL;
      case 'country':
        return companyType !== COMPANY_TYPES.INTERNATIONAL;
      case 'numberOfEmployees':
        return (
          companyType === COMPANY_TYPES.INTERNATIONAL ||
          companyType === COMPANY_TYPES.KSA_PRIVATE
        );
      case 'annualRevenue':
        return (
          companyType === COMPANY_TYPES.INTERNATIONAL ||
          companyType === COMPANY_TYPES.KSA_PRIVATE
        );
      case 'totalInvestmentRaised':
        return companyType === COMPANY_TYPES.INTERNATIONAL;
      default:
        return true;
    }
  };

  const isFieldRequired = (
    fieldName: keyof ExtendedCompanyDetailsFormValues
  ): boolean => {
    const requirements = getFieldRequirements({ companyType });
    const fieldMapping: Record<string, string> = {
      address: 'addressLine1',
    };
    const schemaFieldName = fieldMapping[fieldName] || fieldName;
    const requirement =
      requirements[schemaFieldName as keyof typeof requirements];
    return requirement?.required === true;
  };

  const getFieldLabel = (
    baseLabel: string,
    _fieldName: keyof ExtendedCompanyDetailsFormValues
  ): string => baseLabel;

  return {
    formData,
    errors,
    companyTypeData,
    isValid,
    handleFieldChange,
    handleFieldBlur,
    isFieldRequired,
    shouldRenderField,
    getFieldLabel,
    trigger,
    getValues,
    clearStorage,
  };
};

export default useCompanyFormLogic;
